/**
 * PDF-Ansicht mit Feld-Highlighting und Markierwerkzeug.
 *
 * Das Merkmal, das den Split View von einem bloßen Nebeneinander zweier
 * Fenster unterscheidet: Fährt der Nutzer rechts über ein Feld der
 * Buchungszeile, leuchtet links die passende Stelle im PDF auf.
 *
 * Die Koordinaten kommen als relative Werte (0–1) vom Server und werden hier
 * mit der gerenderten Seitengröße multipliziert. Dadurch sitzt die
 * Hervorhebung bei jedem Zoom und jeder Fensterbreite richtig, ohne dass
 * Server und Oberfläche sich über Pixel einig werden müssten.
 *
 * Kein Download- und kein Drucksymbol: Der Beirat soll den Beleg ansehen,
 * nicht weitergeben.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import PdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

import type { PdfRechteck } from '../lib/typen';

pdfjs.GlobalWorkerOptions.workerSrc = PdfWorker;

export interface PdfAnsichtProps {
  url: string;
  /** Das gerade hervorzuhebende Feld, oder null. */
  hervorhebung: PdfRechteck | null;
  /** Meldet ein aufgezogenes Rechteck für eine Anmerkung. */
  onMarkierung?: (rechteck: PdfRechteck) => void;
  /** Schlüssel, der einen Belegwechsel anzeigt — steuert das Vorladen. */
  belegId: string;
  /**
   * Die gespeicherte Drehung dieses Belegs in Grad.
   *
   * Sie kommt von außen, weil sie zum Dokument gehört und nicht zur Sitzung:
   * Ein quer eingescannter Beleg liegt für jeden Prüfer quer, und wer ihn
   * aufrichtet, tut es für alle folgenden mit.
   */
  drehung?: 0 | 90 | 180 | 270;
  onDrehen?: (grad: 0 | 90 | 180 | 270) => void;
}

/**
 * Ein Rechteck mitdrehen.
 *
 * Die Hervorhebungen kommen in den Koordinaten des ungedrehten Blattes. Wird
 * die Anzeige gedreht, muss das Rechteck mitwandern — sonst leuchtet beim
 * querformatigen Beleg eine beliebige Stelle auf, und das ist schlimmer als
 * keine Hervorhebung, weil es auf das Falsche zeigt.
 */
export function rechteckDrehen(r: PdfRechteck, grad: number): PdfRechteck {
  const g = ((grad % 360) + 360) % 360;
  if (g === 90) {
    return { ...r, x: 1 - (r.y + r.hoehe), y: r.x, breite: r.hoehe, hoehe: r.breite };
  }
  if (g === 180) {
    return { ...r, x: 1 - (r.x + r.breite), y: 1 - (r.y + r.hoehe) };
  }
  if (g === 270) {
    return { ...r, x: r.y, y: 1 - (r.x + r.breite), breite: r.hoehe, hoehe: r.breite };
  }
  return r;
}

export function PdfAnsicht({
  url,
  hervorhebung,
  onMarkierung,
  belegId,
  drehung: drehungAussen = 0,
  onDrehen,
}: PdfAnsichtProps) {
  const leinwand = useRef<HTMLCanvasElement>(null);
  const buehne = useRef<HTMLDivElement>(null);
  const rollflaeche = useRef<HTMLDivElement>(null);
  const dokument = useRef<PDFDocumentProxy | null>(null);
  /** Laufender Renderauftrag, damit ein schneller Belegwechsel nicht doppelt zeichnet. */
  const auftrag = useRef<{ cancel: () => void; promise: Promise<unknown> } | null>(null);
  /**
   * Laufnummer der Zeichenaufrufe. Zwei Aufrufe können sich überholen —
   * Seitenwechsel und ResizeObserver feuern oft im selben Moment — und
   * pdf.js weigert sich, zweimal gleichzeitig auf dieselbe Leinwand zu
   * zeichnen. Nur der jüngste Aufruf darf am Ende zeichnen.
   */
  const laufnummer = useRef(0);
  const [fehlerDetail, setFehlerDetail] = useState<string | null>(null);

  const [seite, setSeite] = useState(1);
  const [seiten, setSeiten] = useState(1);
  const [zoom, setZoom] = useState(1);
  const drehung = drehungAussen;
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [groesse, setGroesse] = useState({ breite: 0, hoehe: 0 });
  const [markierung, setMarkierung] = useState<
    { x: number; y: number; breite: number; hoehe: number } | null
  >(null);

  // ── Dokument laden ───────────────────────────────────────
  useEffect(() => {
    let verworfen = false;
    setLaedt(true);
    setFehler(null);
    setSeite(1);

    const aufgabe = pdfjs.getDocument({ url, withCredentials: false });
    aufgabe.promise.then(
      (doc) => {
        if (verworfen) {
          doc.destroy();
          return;
        }
        dokument.current?.destroy();
        dokument.current = doc;
        setSeiten(doc.numPages);
        setLaedt(false);
      },
      (grund: Error) => {
        if (verworfen) return;
        setLaedt(false);
        setFehler(
          grund.name === 'MissingPDFException'
            ? 'Zu diesem Beleg ist kein PDF hinterlegt.'
            : 'Das PDF konnte nicht geladen werden.',
        );
      },
    );

    return () => {
      verworfen = true;
      aufgabe.destroy().catch(() => undefined);
    };
  }, [url]);

  // ── Seite zeichnen ───────────────────────────────────────
  const zeichnen = useCallback(async () => {
    const doc = dokument.current;
    const ziel = leinwand.current;
    if (!doc || !ziel) return;
    const meine = ++laufnummer.current;

    // Einen noch laufenden Auftrag abbrechen und sein Ende abwarten: Beim
    // schnellen Blättern überholen sich sonst zwei Renderläufe, und pdf.js
    // bricht den zweiten ab (dieselbe Leinwand, zwei render-Aufrufe).
    const laufend = auftrag.current;
    if (laufend) {
      laufend.cancel();
      await laufend.promise.catch(() => undefined);
    }
    if (meine !== laufnummer.current) return;

    let pdfSeite;
    try {
      pdfSeite = await doc.getPage(Math.min(seite, doc.numPages));
    } catch (grund) {
      if (meine !== laufnummer.current) return;
      setFehler('Die Seite konnte nicht geladen werden.');
      setFehlerDetail((grund as Error).message ?? null);
      return;
    }
    if (meine !== laufnummer.current) return;
    const basis = pdfSeite.getViewport({ scale: 1, rotation: drehung });

    // Verfügbare Breite als Ausgangsmaß, damit das Blatt den Bereich füllt.
    const verfuegbar = (rollflaeche.current?.clientWidth ?? 900) - 32;
    const massstab = (verfuegbar / basis.width) * zoom;
    const ansicht = pdfSeite.getViewport({ scale: massstab, rotation: drehung });

    // Auf Bildschirmen mit hoher Pixeldichte doppelt so fein zeichnen,
    // sonst wirkt der Beleg unscharf und wird als schlechter Scan gelesen.
    const dichte = Math.min(window.devicePixelRatio || 1, 2);
    ziel.width = Math.floor(ansicht.width * dichte);
    ziel.height = Math.floor(ansicht.height * dichte);
    ziel.style.width = `${Math.floor(ansicht.width)}px`;
    ziel.style.height = `${Math.floor(ansicht.height)}px`;

    const kontext = ziel.getContext('2d');
    if (!kontext) return;

    let lauf: { cancel: () => void; promise: Promise<unknown> };
    try {
      lauf = pdfSeite.render({
        canvasContext: kontext,
        viewport: ansicht,
        transform: dichte !== 1 ? [dichte, 0, 0, dichte, 0, 0] : undefined,
      });
    } catch (grund) {
      // Die Leinwand ist noch belegt — der nächste Aufruf räumt auf.
      console.warn('PDF: render() abgewiesen', grund);
      return;
    }
    auftrag.current = lauf;

    try {
      await lauf.promise;
      if (meine === laufnummer.current) {
        setGroesse({ breite: ansicht.width, hoehe: ansicht.height });
        setFehler(null);
        setFehlerDetail(null);
      }
    } catch (grund) {
      // Ein abgebrochener Auftrag ist der Normalfall beim Blättern.
      if ((grund as Error).name !== 'RenderingCancelledException' && meine === laufnummer.current) {
        console.warn('PDF: Seite nicht darstellbar', grund);
        setFehler('Die Seite konnte nicht dargestellt werden.');
        setFehlerDetail((grund as Error).message ?? null);
      }
    } finally {
      if (auftrag.current === lauf) auftrag.current = null;
    }
  }, [seite, zoom, drehung]);

  // Ein Fehler gilt für eine Seite, nicht für das Dokument: Beim Blättern
  // oder Zoomen wird neu versucht.
  useEffect(() => {
    setFehler(null);
    setFehlerDetail(null);
  }, [seite, zoom, drehung, url]);

  useLayoutEffect(() => {
    if (!laedt && !fehler) void zeichnen();
  }, [laedt, fehler, zeichnen]);

  // Bei Größenänderung des Fensters neu zeichnen, damit das Blatt weiterhin
  // die Breite füllt und die Hervorhebung nicht verrutscht.
  useEffect(() => {
    const flaeche = rollflaeche.current;
    if (!flaeche) return;
    const beobachter = new ResizeObserver(() => {
      if (!laedt && !fehler) void zeichnen();
    });
    beobachter.observe(flaeche);
    return () => beobachter.disconnect();
  }, [laedt, fehler, zeichnen]);

  // Beim Belegwechsel zurück auf Seite 1, Markierung verwerfen.
  useEffect(() => {
    setSeite(1);
    setMarkierung(null);
  }, [belegId]);

  // Springt zur Seite, auf der die Hervorhebung liegt.
  useEffect(() => {
    if (hervorhebung && hervorhebung.seite !== seite) setSeite(hervorhebung.seite);
  }, [hervorhebung, seite]);

  // ── Markierwerkzeug ──────────────────────────────────────
  const zeichenStart = useRef<{ x: number; y: number } | null>(null);

  function beiMausAb(e: React.MouseEvent) {
    if (e.button !== 0 || !buehne.current) return;
    const r = buehne.current.getBoundingClientRect();
    zeichenStart.current = { x: e.clientX - r.left, y: e.clientY - r.top };
    setMarkierung({ x: zeichenStart.current.x, y: zeichenStart.current.y, breite: 0, hoehe: 0 });
    e.preventDefault();
  }

  useEffect(() => {
    function bewegen(e: MouseEvent) {
      const start = zeichenStart.current;
      if (!start || !buehne.current) return;
      const r = buehne.current.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      setMarkierung({
        x: Math.min(x, start.x),
        y: Math.min(y, start.y),
        breite: Math.abs(x - start.x),
        hoehe: Math.abs(y - start.y),
      });
    }
    function loslassen() {
      const start = zeichenStart.current;
      zeichenStart.current = null;
      if (!start) return;
      setMarkierung((m) => {
        // Ein Klick ohne Ziehen ist keine Markierung — und ohne Abnehmer
        // (abgeschlossene Prüfung) bleibt kein Rechteck stehen.
        if (!m || m.breite < 14 || m.hoehe < 10 || !onMarkierung) return null;
        // Zurückgerechnet auf das ungedrehte Blatt: Gespeichert wird in den
        // Koordinaten des Dokuments, nicht in denen der gerade eingestellten
        // Ansicht — sonst zeigte dieselbe Anmerkung nach dem nächsten Drehen
        // woandershin.
        onMarkierung?.(
          rechteckDrehen(
            {
              seite,
              x: m.x / groesse.breite,
              y: m.y / groesse.hoehe,
              breite: m.breite / groesse.breite,
              hoehe: m.hoehe / groesse.hoehe,
            },
            360 - drehung,
          ),
        );
        return m;
      });
    }
    window.addEventListener('mousemove', bewegen);
    window.addEventListener('mouseup', loslassen);
    return () => {
      window.removeEventListener('mousemove', bewegen);
      window.removeEventListener('mouseup', loslassen);
    };
  }, [groesse, seite, onMarkierung, drehung]);

  // ── Darstellung ──────────────────────────────────────────
  const zeigtHervorhebung = hervorhebung && hervorhebung.seite === seite && groesse.breite > 0;
  const hl = zeigtHervorhebung ? rechteckDrehen(hervorhebung, drehung) : null;

  return (
    <div className="pane pane-pdf">
      <div className="pdfbar">
        <button
          className="navbtn"
          onClick={() => setSeite((s) => Math.max(1, s - 1))}
          disabled={seite <= 1}
          aria-label="Seite zurück"
        >
          ‹
        </button>
        <span className="num">
          Seite <b>{seite}</b> von {seiten}
        </span>
        <button
          className="navbtn"
          onClick={() => setSeite((s) => Math.min(seiten, s + 1))}
          disabled={seite >= seiten}
          aria-label="Seite vor"
        >
          ›
        </button>

        <div className="sep" />

        <button
          className="navbtn"
          onClick={() => setZoom((z) => Math.max(0.5, Number((z - 0.15).toFixed(2))))}
          aria-label="Verkleinern"
        >
          −
        </button>
        <span className="num" style={{ minWidth: 44, textAlign: 'center' }}>
          {Math.round(zoom * 100)} %
        </span>
        <button
          className="navbtn"
          onClick={() => setZoom((z) => Math.min(3, Number((z + 0.15).toFixed(2))))}
          aria-label="Vergrößern"
        >
          +
        </button>
        <button className="btn ghost sm pdfbar-breite" style={{ height: 26 }} onClick={() => setZoom(1)} title="An Breite anpassen">
          <span className="lang">An Breite anpassen</span>
          <span className="kurz">Breite</span>
        </button>

        <div className="sep" />
        <button
          className="btn ghost sm"
          style={{ height: 26 }}
          onClick={() => onDrehen?.((((drehung + 90) % 360) as 0 | 90 | 180 | 270))}
          disabled={!onDrehen}
          title="Quer eingescannte Belege aufrichten. Die Drehung gilt für alle Prüfer; das Dokument selbst bleibt unverändert."
        >
          ⟳ Drehen
        </button>
        {drehung !== 0 && (
          <span className="num" style={{ color: 'var(--tx-3)' }}>
            {drehung}°
          </span>
        )}

        <span className="pdfbar-hinweis" title="Mit gedrückter Maustaste ein Rechteck aufziehen, um eine Stelle für eine Rückfrage zu markieren">
          Rechteck aufziehen für eine Anmerkung
        </span>
      </div>

      <div className="pdfscroll" ref={rollflaeche}>
        {fehler ? (
          <div className="leer card">
            <h3>{fehler}</h3>
            <p>
              {seiten > 1 ? `Seite ${seite} von ${seiten}. ` : ''}
              Sie können es noch einmal versuchen oder weiterblättern; die übrigen Seiten sind davon nicht betroffen.
            </p>
            {fehlerDetail && <p className="note mono" style={{ fontSize: 11 }}>{fehlerDetail}</p>}
            <button
              className="btn sm"
              onClick={() => {
                setFehler(null);
                setFehlerDetail(null);
              }}
            >
              Erneut versuchen
            </button>
          </div>
        ) : laedt ? (
          <div
            className="skelett"
            style={{ width: 'min(640px, 100%)', aspectRatio: '1 / 1.414' }}
            aria-label="Beleg wird geladen"
          />
        ) : (
          <div className="pdfstage" ref={buehne} onMouseDown={beiMausAb}>
            <canvas ref={leinwand} />

            {hl && (
              <div
                className="hl"
                style={{
                  left: hl.x * groesse.breite,
                  top: hl.y * groesse.hoehe,
                  width: hl.breite * groesse.breite,
                  height: hl.hoehe * groesse.hoehe,
                }}
              />
            )}

            {markierung && (
              <div
                className="mark"
                style={{
                  left: markierung.x,
                  top: markierung.y,
                  width: markierung.breite,
                  height: markierung.hoehe,
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
